import React, { useRef, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    Star,
    CheckCircle,
    Heart,
    Globe2,
    Circle,
} from "lucide-react";

export default function HorizontalSection({ title, data = [] }) {
    const scrollRef = useRef(null);
    const [likes, setLikes] = useState({});

    const scroll = (dir) => {
        if (!scrollRef.current) return;
        const amount = dir === "left" ? -350 : 350;
        scrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    };

    const toggleLike = (id) => {
        setLikes((prev) => ({
            ...prev,
            [id]: prev[id] ? prev[id] + 1 : 1,
        }));
    };

    return (
        <section className="relative group">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg sm:text-xl font-bold text-blue-300">{title}</h2>
            </div>

            {/* Scroll buttons */}
            <button
                onClick={() => scroll("left")}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-900/70 border border-slate-700 hover:bg-slate-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition"
            >
                <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
                onClick={() => scroll("right")}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-900/70 border border-slate-700 hover:bg-slate-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition"
            >
                <ChevronRight className="w-5 h-5 text-white" />
            </button>

            {/* Scrollable cards */}
            <div
                ref={scrollRef}
                className="flex gap-4 overflow-x-auto no-scrollbar scroll-smooth pb-4 px-1 sm:px-0"
                style={{
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {data.map((item, i) => (
                    <div
                        key={i}
                        className="min-w-[80%] sm:min-w-[260px] bg-slate-900/60 rounded-2xl border border-slate-800 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/20 transition"
                    >
                        {/* Image + Info remain same */}
                        <div className="relative">
                            <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-40 sm:h-36 object-cover rounded-t-2xl"
                            />
                            <span
                                className={`absolute top-2 left-2 flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${item.online
                                        ? "bg-green-500/80 text-white"
                                        : "bg-slate-600/70 text-white"
                                    }`}
                            >
                                <span className="w-2 h-2 rounded-full bg-current"></span>
                                {item.online ? "Online" : "Offline"}
                            </span>
                        </div>
                        <div className="p-3">
                            <div className="flex justify-between items-center">
                                <h3 className="font-semibold text-white truncate">{item.title}</h3>
                                <span
                                    className={`text-[10px] px-2 py-1 rounded-full ${item.tier === "PLATINUM"
                                            ? "bg-blue-600"
                                            : item.tier === "GOLD"
                                                ? "bg-yellow-500"
                                                : item.tier === "ROYAL"
                                                    ? "bg-purple-600"
                                                    : "bg-gray-500"
                                        }`}
                                >
                                    {item.tier}
                                </span>
                            </div>

                            <p className="text-slate-400 text-xs mt-1 line-clamp-2">{item.desc}</p>

                            <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                                🌍 {item.country || "Global"}
                            </div>

                            <div className="flex justify-between items-center mt-3 text-xs">
                                <button className="px-2 py-0.5 border border-slate-600 rounded-full hover:border-blue-500 text-blue-400 text-xs">
                                    Follow
                                </button>
                                <button className="text-pink-400 hover:text-pink-300">♥</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </section>
    );
}
